import {
    type IFetchInvestorCashflowParams,
    createInvestorCashflowForInvestment,
    fetchInvestorCashflowsForEstate,
    generateInvestorCashflowSheet,
    getInvestorCashflow,
    recalculateInvestorCashflow as serviceRecalculateInvestorCashflow,
} from '@/api/investor-cashflow.api';
import { withLoading } from '@/shared';
import type { IInvestorCashflowCalculationInputs, InvestorCashflow } from '@condo/domain';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { type Ref, computed, isRef } from 'vue';

export const useInvestorCashflowList = (estateId: number, paramsOrRef?: IFetchInvestorCashflowParams | Ref<IFetchInvestorCashflowParams>) => {
    const queryClient = useQueryClient();

    const params = computed(() => (isRef(paramsOrRef) ? paramsOrRef.value : paramsOrRef));

    const query = useQuery(
        ['investor-cashflows', estateId, params],
        async (): Promise<{ investorCashflows: InvestorCashflow[]; count: number }> =>
            withLoading(async () => fetchInvestorCashflowsForEstate(estateId, params.value)),
        {
            initialData: { investorCashflows: [], count: 0 },
        },
    );

    const { mutateAsync: createInvestorCashflow } = useMutation(
        async ({ investmentId }: { investmentId: number }) => createInvestorCashflowForInvestment(investmentId),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['investor-cashflows', estateId]);
            },
        },
    );

    const { mutateAsync: generateSheetMutation } = useMutation(async (investorCashflowId: number) => generateInvestorCashflowSheet(investorCashflowId), {
        onSuccess: () => {
            queryClient.invalidateQueries(['investor-cashflows', estateId]);
        },
    });

    const { mutateAsync: recalculateInvestorCashflow } = useMutation(
        async ({ investorCashflowId, opts }: { investorCashflowId: number; opts: { preserveInputs?: boolean; fullRecalculation?: boolean } }) =>
            serviceRecalculateInvestorCashflow(investorCashflowId, undefined, opts),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['investor-cashflows', estateId]);
            },
        },
    );

    return { ...query, createInvestorCashflow, generateSheet: generateSheetMutation, recalculateInvestorCashflow };
};

export const useInvestorCashflow = (estateId: number, investorCashflowId: number) => {
    const queryClient = useQueryClient();

    const query = useQuery(
        [`investor-cashflows/${estateId}/${investorCashflowId}`],
        async (): Promise<InvestorCashflow> => withLoading(async () => getInvestorCashflow(estateId, investorCashflowId)),
        {
            initialData: undefined,
        },
    );

    const { mutateAsync } = useMutation(
        async ({
            inputs,
            opts,
        }: {
            inputs?: Partial<IInvestorCashflowCalculationInputs>;
            opts: {
                fullRecalculation?: boolean;
                persistAfterRecalculation?: boolean;
                preserveInputs?: boolean;
            };
        }) => serviceRecalculateInvestorCashflow(investorCashflowId, inputs, opts),
        {
            onSuccess: () => {
                queryClient.invalidateQueries([`investor-cashflows/${estateId}/${investorCashflowId}`]);
            },
        },
    );

    return { ...query, recalculateInvestorCashflow: mutateAsync };
};
